<h2 mat-dialog-title class="help-title">
  <mat-icon>help_outline</mat-icon>
  Comment créer une requête
</h2>

<mat-dialog-content class="help-content">
  <mat-tab-group animationDuration="150ms">
    <!-- ── Vue d'ensemble ─────────────────────────────────── -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="tab-icon">info</mat-icon>
        Vue d'ensemble
      </ng-template>
      <div class="tab-body">
        <p class="intro">
          Une requête est du <strong>SQL standard</strong> envoyé à la base de données choisie. Des
          <strong>tags spéciaux</strong> permettent d'y insérer des champs interactifs que l'utilisateur remplira avant
          d'exécuter.
        </p>

        <div class="section">
          <h3>Exemple minimal</h3>
          <pre><code>SELECT id, nom, date
FROM ma_table
LIMIT 100</code></pre>
          <p class="hint">
            Toute requête doit commencer par <code>SELECT</code> — les opérations d'écriture sont bloquées.
          </p>
        </div>

        <div class="section">
          <h3>Tags disponibles</h3>
          <table class="tags-table">
            <thead>
              <tr>
                <th>Tag</th>
                <th>Rôle</th>
                <th>Bouton</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>&#123;field type="text" …&#125;</code></td>
                <td>Champ texte saisi par l'utilisateur</td>
                <td><span class="chip">text</span></td>
              </tr>
              <tr>
                <td><code>&#123;field type="number" …&#125;</code></td>
                <td>Entier saisi par l'utilisateur, substitué sans guillemets</td>
                <td><span class="chip">number</span></td>
              </tr>
              <tr>
                <td><code>&#123;field type="textlist" …&#125;</code></td>
                <td>Liste de valeurs pour une clause <code>IN</code></td>
                <td><span class="chip">textlist</span></td>
              </tr>
              <tr>
                <td><code>&#123;field type="numberlist" …&#125;</code></td>
                <td>Liste de nombres pour une clause <code>IN</code> (sans guillemets)</td>
                <td><span class="chip">numberlist</span></td>
              </tr>
              <tr>
                <td><code>&#123;field type="date" …&#125;</code></td>
                <td>Sélecteur de date</td>
                <td><span class="chip">date</span></td>
              </tr>
              <tr>
                <td><code>&#123;env …&#125;</code></td>
                <td>Sélecteur d'environnement (dev / ppd / prd)</td>
                <td><span class="chip">env</span></td>
              </tr>
              <tr>
                <td><code>&#123;link …&#125;</code></td>
                <td>Lien cliquable dans les résultats</td>
                <td><span class="chip">link</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </mat-tab>

    <!-- ── Champ texte ────────────────────────────────────── -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="tab-icon">text_fields</mat-icon>
        Champ texte
      </ng-template>
      <div class="tab-body">
        <p class="intro">
          <code>&#123;field type="text"&#125;</code> affiche un champ libre à l'utilisateur. La valeur saisie est
          injectée dans le SQL entre guillemets simples.
        </p>

        <div class="section">
          <h3>Syntaxe</h3>
          <pre><code>&#123;field type="text" name="<em>id_interne</em>" label="<em>Libellé</em>" default="<em>valeur</em>" description="<em>aide</em>" prefix="<em>avant</em>" suffix="<em>après</em>"&#125;</code></pre>
          <ul class="attrs">
            <li><code>name</code> — identifiant unique du champ (lettres, chiffres, _)</li>
            <li><code>label</code> — libellé affiché à l'utilisateur <span class="optional">(optionnel)</span></li>
            <li><code>default</code> — valeur pré-remplie <span class="optional">(optionnel)</span></li>
            <li>
              <code>description</code> — texte d'aide affiché en infobulle <span class="optional">(optionnel)</span>
            </li>
            <li>
              <code>prefix</code> — texte ajouté avant la valeur saisie dans le SQL
              <span class="optional">(optionnel)</span>
            </li>
            <li>
              <code>suffix</code> — texte ajouté après la valeur saisie dans le SQL
              <span class="optional">(optionnel)</span>
            </li>
          </ul>
        </div>

        <div class="section">
          <h3>Exemple simple</h3>
          <pre><code>SELECT *
FROM pds
WHERE id = &#123;field type="text" name="pds_id" label="Identifiant PDS" default="BREST_EP-001"&#125;</code></pre>
          <div class="renders-as"><mat-icon>arrow_downward</mat-icon> SQL exécuté</div>
          <pre class="rendered"><code>SELECT *
FROM pds
WHERE id = 'BREST_EP-001'</code></pre>
        </div>

        <div class="section">
          <h3>Exemple avec préfixe et suffixe</h3>
          <pre><code>SELECT *
FROM pds
WHERE id = &#123;field type="text" name="pds_id" label="Identifiant PDS" prefix="PDS_" suffix="_FR"&#125;</code></pre>
          <p class="hint">
            L'utilisateur saisit <code>BREST_EP-001</code> — le préfixe et le suffixe sont ajoutés automatiquement.
          </p>
          <div class="renders-as"><mat-icon>arrow_downward</mat-icon> SQL exécuté</div>
          <pre class="rendered"><code>SELECT *
FROM pds
WHERE id = 'PDS_BREST_EP-001_FR'</code></pre>
        </div>
      </div>
    </mat-tab>

    <!-- ── Entier ───────────────────────────────────────────── -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="tab-icon">pin</mat-icon>
        Entier
      </ng-template>
      <div class="tab-body">
        <p class="intro">
          <code>&#123;field type="number"&#125;</code> affiche un champ numérique entier. La valeur est injectée
          <strong>sans guillemets</strong> dans le SQL — idéal pour les clauses <code>LIMIT</code>, <code>OFFSET</code>,
          ou les comparaisons numériques.
        </p>

        <div class="section">
          <h3>Syntaxe</h3>
          <pre><code>&#123;field type="number" name="<em>id_interne</em>" label="<em>Libellé</em>" default="<em>valeur</em>" description="<em>aide</em>"&#125;</code></pre>
          <ul class="attrs">
            <li><code>name</code> — identifiant unique du champ (lettres, chiffres, _)</li>
            <li><code>label</code> — libellé affiché à l'utilisateur <span class="optional">(optionnel)</span></li>
            <li><code>default</code> — valeur entière pré-remplie <span class="optional">(optionnel)</span></li>
            <li>
              <code>description</code> — texte d'aide affiché en infobulle <span class="optional">(optionnel)</span>
            </li>
          </ul>
        </div>

        <div class="section">
          <h3>Exemple</h3>
          <pre><code>SELECT *
FROM releves
WHERE compteur_id = &#123;field type="number" name="compteur_id" label="ID compteur"&#125;
LIMIT &#123;field type="number" name="limite" label="Nombre de lignes" default="100"&#125;</code></pre>
          <div class="renders-as"><mat-icon>arrow_downward</mat-icon> SQL exécuté</div>
          <pre class="rendered"><code>SELECT *
FROM releves
WHERE compteur_id = 42
LIMIT 100</code></pre>
        </div>
      </div>
    </mat-tab>

    <!-- ── Liste de valeurs ───────────────────────────────── -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="tab-icon">list</mat-icon>
        Liste de valeurs
      </ng-template>
      <div class="tab-body">
        <p class="intro">
          <code>&#123;field type="textlist"&#125;</code> affiche une zone de texte multiligne. Chaque ligne devient une
          valeur dans une clause <code>IN (…)</code>.
        </p>

        <div class="section">
          <h3>Syntaxe</h3>
          <pre><code>&#123;field type="textlist" name="<em>id_interne</em>" label="<em>Libellé</em>"&#125;</code></pre>
        </div>

        <div class="section">
          <h3>Exemple</h3>
          <pre><code>SELECT *
FROM pds
WHERE id IN &#123;field type="textlist" name="ids" label="Identifiants PDS"&#125;</code></pre>
          <p class="hint">L'utilisateur saisit une valeur par ligne :</p>
          <pre><code>BREST_EP-001
BREST_EP-002
BREST_EP-003</code></pre>
          <div class="renders-as"><mat-icon>arrow_downward</mat-icon> SQL exécuté</div>
          <pre class="rendered"><code>SELECT *
FROM pds
WHERE id IN ('BREST_EP-001', 'BREST_EP-002', 'BREST_EP-003')</code></pre>
        </div>
      </div>
    </mat-tab>

    <!-- ── Liste de nombres ──────────────────────────────── -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="tab-icon">format_list_numbered</mat-icon>
        Liste de nombres
      </ng-template>
      <div class="tab-body">
        <p class="intro">
          <code>&#123;field type="numberlist"&#125;</code> affiche une zone de texte multiligne. Chaque ligne devient un
          nombre dans une clause <code>IN (…)</code> — les valeurs sont injectées <strong>sans guillemets</strong>. Le
          backend vérifie que chaque ligne est bien un nombre valide.
        </p>

        <div class="section">
          <h3>Syntaxe</h3>
          <pre><code>&#123;field type="numberlist" name="<em>id_interne</em>" label="<em>Libellé</em>"&#125;</code></pre>
        </div>

        <div class="section">
          <h3>Exemple</h3>
          <pre><code>SELECT *
FROM releves
WHERE compteur_id IN &#123;field type="numberlist" name="ids" label="IDs compteurs"&#125;</code></pre>
          <p class="hint">L'utilisateur saisit un nombre par ligne :</p>
          <pre><code>1042
2891
3754</code></pre>
          <div class="renders-as"><mat-icon>arrow_downward</mat-icon> SQL exécuté</div>
          <pre class="rendered"><code>SELECT *
FROM releves
WHERE compteur_id IN (1042, 2891, 3754)</code></pre>
        </div>
      </div>
    </mat-tab>

    <!-- ── Date ───────────────────────────────────────────── -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="tab-icon">calendar_today</mat-icon>
        Date
      </ng-template>
      <div class="tab-body">
        <p class="intro">
          <code>&#123;field type="date"&#125;</code> affiche un sélecteur de date. La valeur est injectée au format
          <code>YYYY-MM-DD</code> entre guillemets simples.
        </p>

        <div class="section">
          <h3>Syntaxe</h3>
          <pre><code>&#123;field type="date" name="<em>id_interne</em>" label="<em>Libellé</em>" default="<em>dd-MM-yyyy</em>"&#125;</code></pre>
          <p class="hint">
            La valeur par défaut s'écrit au format <code>dd-MM-yyyy</code> (ex : <code>01-01-2024</code>).
          </p>
        </div>

        <div class="section">
          <h3>Exemple</h3>
          <pre><code>SELECT *
FROM releves
WHERE date_releve BETWEEN
  &#123;field type="date" name="date_debut" label="Date de début" default="01-01-2024"&#125;
  AND
  &#123;field type="date" name="date_fin" label="Date de fin"&#125;</code></pre>
          <div class="renders-as"><mat-icon>arrow_downward</mat-icon> SQL exécuté</div>
          <pre class="rendered"><code>SELECT *
FROM releves
WHERE date_releve BETWEEN '2024-01-01' AND '2024-06-30'</code></pre>
        </div>
      </div>
    </mat-tab>

    <!-- ── Environnement ──────────────────────────────────── -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="tab-icon">layers</mat-icon>
        Environnement
      </ng-template>
      <div class="tab-body">
        <p class="intro">
          <code>&#123;env&#125;</code> permet de choisir l'environnement cible. Utile quand les noms de tables ou de
          datasets varient entre dev, ppd et prd.
        </p>

        <div class="section">
          <h3>Syntaxe</h3>
          <pre><code>&#123;env values="dev,ppd,prd" default="prd"&#125;</code></pre>
          <ul class="attrs">
            <li>
              <code>values</code> — liste des environnements autorisés, séparés par des virgules
              <span class="optional">(optionnel, défaut : dev, ppd, prd)</span>
            </li>
            <li>
              <code>default</code> — valeur pré-sélectionnée
              <span class="optional">(optionnel, défaut : dernier de la liste)</span>
            </li>
          </ul>
        </div>

        <div class="section">
          <h3>Exemple</h3>
          <pre><code>SELECT *
FROM mon_projet_&#123;env&#125;.ma_table
LIMIT 100</code></pre>
          <div class="renders-as"><mat-icon>arrow_downward</mat-icon> SQL exécuté (environnement prd sélectionné)</div>
          <pre class="rendered"><code>SELECT *
FROM mon_projet_prd.ma_table
LIMIT 100</code></pre>
        </div>
      </div>
    </mat-tab>

    <!-- ── Lien ───────────────────────────────────────────── -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="tab-icon">link</mat-icon>
        Lien
      </ng-template>
      <div class="tab-body">
        <p class="intro">
          <code>&#123;link&#125;</code> transforme une colonne en lien cliquable dans les résultats. L'URL est calculée
          par la base de données à partir des données de chaque ligne.
        </p>

        <div class="section">
          <h3>Syntaxe</h3>
          <pre><code>&#123;link url="<em>expression SQL</em>" label="<em>Texte du lien</em>"&#125;</code></pre>
          <ul class="attrs">
            <li><code>url</code> — expression SQL qui construit l'URL (ex : <code>CONCAT('https://…/', id)</code>)</li>
            <li><code>label</code> — texte affiché dans la cellule à la place de l'URL brute</li>
          </ul>
          <p class="hint">Le label devient automatiquement le nom de la colonne dans le tableau.</p>
        </div>

        <div class="section">
          <h3>Exemple</h3>
          <pre><code>SELECT
  id,
  &#123;link url="CONCAT('https://pds.apps.birdz.com/consumption?pds=', id)" label="Voir PDS360"&#125;
FROM pds
LIMIT 10</code></pre>
          <div class="renders-as"><mat-icon>arrow_downward</mat-icon> Résultat dans le tableau</div>
          <div class="link-preview">
            <table class="preview-table">
              <thead>
                <tr>
                  <th>id</th>
                  <th>Voir PDS360</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>BREST_EP-001</td>
                  <td><a>Voir PDS360</a></td>
                </tr>
                <tr>
                  <td>BREST_EP-002</td>
                  <td><a>Voir PDS360</a></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </mat-tab>
  </mat-tab-group>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-flat-button mat-dialog-close>Fermer</button>
</mat-dialog-actions>
